import React, { useState, useMemo } from 'react'
import { Drawer, Tabs, DatePicker, Menu, Dropdown, Badge } from 'antd'
import Select from '@atlaskit/select'
import ReactSelect from 'react-select'
import { useSelector } from 'react-redux'
import pluralize from 'pluralize'
import { Link, Route, Switch, useHistory, useParams } from 'react-router-dom'
import { AiOutlineDown } from 'react-icons/ai'

import { conversionRate, normalize, stringifyDate, useListData, usePet, useToggle } from '../../modules'

import Receive from '../reusable/receive'
import Confirm from '../reusable/confirm'

import EditPrescription from '../modals/editPrescription'
import Elevated from '../modals/elevated'
import EditDrugsQty from '../modals/editDrugsQty'
import CancelItem from '../modals/cancelItem'
import IssueBatchPicker from '../modals/issueBatchPicker'

import PatientDetails from './patient'

const { TabPane } = Tabs
const { RangePicker } = DatePicker

const SentMedication = ({ source, onPerform, tab: tb = "1" }) => {
    let group = 'Medication'

    const [ranges, setRanges] = useState()
    const [cat, setCat] = useState({ label: 'All', value: 'All' })
    const [selected, setSelected] = useState([])
    const [si, setSI] = useState()
    const [index, setIndex] = useState()
    const [categories, setCategories] = useState([])
    const [ids, setIds] = useState([])
    const [pres, setPres] = useState()
    const [order, setOrder] = useState()
    const [string, setString] = useState()
    const [sString, setSString] = useState()
    const [tab, setTab] = useState(tb)
    const [reason, setReason] = useState()
    const [onSuccess, setOnSuccess] = useState(() => null)
    const [token, setToken] = useState()
    const [reload, setReload] = useState(false)
    const [local, setLocal] = useState(false)
    
    const { id } = useParams()
    const history = useHistory()

    const { shift } = useSelector(state => state.application)
    
    const [elevated, toggleElevated] = useToggle()
    const [visible, toggleVisible] = useToggle(true)
    const [includeOthers, toggleOthers] = useToggle()
    const [prescription, togglePres] = useToggle()
    const [editQty, toggleQty] = useToggle()
    const [cancel, toggleCancel] = useToggle()
    const [batchPicker, toggleBatchPicker] = useToggle()
    const [select, /* toggleSelect*/] = useToggle()

    const [pharmacyStoreId, setPharmacyStoreId] = useState()
    usePet('pharmacyStore', {}, { notify: false, callback: ({ data }) => setPharmacyStoreId(data?._id) })

    const close = () => {
        if (reload)
            onPerform()

        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }

    const [, markAsSeen] = usePet('markAsSeen', { ids }, { notify: true, callback: () => null, manual: true })

    useMemo(() => setTimeout(markAsSeen, 5000), [])

    const [, getOrder] = usePet('order', { id }, { notify: false, callback: ({ data }) => setOrder(data) })

    usePet('medicationGroup', null, { notify: false, callback: ({ data }) => setCategories(data.categories) })

    const [
        { loading: iLoading, fetch: fetchItems },
        { list: items },
        { page = 1, limit = 10 }
    ] = useListData('saleItems', { string, category: cat.label, group }, { notify: true }, 'items')

    const [
        { loading: itLoading, error: itError, fetch: fetchBillItems },
        { list: billItems, count = 0 },
        { page: itPage = 1, limit: itLimit = 10, navigate, Paginate }
    ] = useListData('billedItems', ranges ? { 
        string: sString, 
        bill: includeOthers ? undefined : id,
        group,
        from: ranges[0]?._d ?? null, 
        to: ranges[1]?._d ?? null
    } : { string: sString, bill: includeOthers ? undefined : id, group }, { notify: true, callback: ({ data }) => setIds(data.items.map(itm => itm._id)) }, 'items')

    const refresh = () => {
        navigate(1)
        fetchBillItems()
    }

    const elevate = fn => {
        setOnSuccess({ fn, setToken })
        setTimeout(toggleElevated, 200)
    }

    const onComplete = (_, notif, ops) => {
        notif(`Request${selected?.length > 1 ? 's' : '' } for the specified items ${selected?.length > 1 ? 'have' : 'has' } been sent`, ops('success'))
        clear()
        refresh()
        setReload(true)
        setTab("1")
    }
    
    const [, billPatient] = usePet('billPatient', {
        patient: order?.patient?._id,
        items: selected.map(({ _id, quantity, price, instructions }) => ({
            item: _id,
            quantity,
            instructions,
            price
        })),
        station: localStorage.getItem('station') ?? 'Cashier'
    }, { notify: true, callback: onComplete, manual: true, load: { msg: 'Adding items to bill...' } })

    const [{ loading: cLoading }, toggleCancelBilledItem] = usePet('toggleCancelBilledItem', {
        id: pres?._id,
        reason
    }, { notify: true, callback: ({ data }, notif, ops) => {
        notif(data?.cancelled ? 'Prescription has been cancelled' : 'Cancel action has been reversed', ops('success'))
        fetchBillItems()
    }, manual: true, load: { msg: pres?.cancelled ? 'Reversing cancel action' : 'Cancelling item' }, token })

    const [{ loading: ipLoading }, issuePrescription] = usePet('issuePrescription', {
        id: pres?._id
    }, { notify: true, callback: ({ data }, notif, ops) => {
        notif('Pharmacy inventory has been updated', ops('success'))
        toggleBatchPicker()
        fetchBillItems()
    }, manual: true, load: { msg: 'Updating pharmacy inventory' }, token })

    const [{ loading: presLoading }, returnPrescription] = usePet('returnPrescription', {
        id: pres?._id
    }, { notify: true, callback: ({ data }, notif, ops) => {
        notif('Return has been recorded', ops('success'))
        fetchBillItems()
    }, manual: true, load: { msg: 'Recording returns' }, token })

    const [{ loading: upLoading }, updatePrescription] = usePet('updatePrescription', {
        id: pres?._id,
        quantity: pres?.quantity,
        instructions: pres?.instructions
    }, { notify: true, callback: ({ data }, notif, ops) => {
        notif('Prescription has been updated', ops('success'))
        fetchBillItems()
    }, manual: true, load: { msg: 'Updating prescription' }, token })

    const searchSent = str => {
        if (str?.trim()?.length > 2 || !str) {
            setSString(str)
            refresh()
        }  
    }

    const search = str => {
        if (str?.trim()?.length > 2 || !str) {
            setString(str)
            fetchItems()
        }  
    }

    const onCategoryChange = ct => {
        setCat(ct)
        fetchItems()
    }

    // const editPrescription = e => {
    //     let temp = [...selected]

    //     let [prop, i] = e.target.name.split('-')
    //     i = Number(i)

    //     temp[i][prop] = Number(e.target.value)

    //     let ref = temp[i]
    //     temp[i].instructions = `${ref.dosage} x ${ref.frequency} for ${ref.days} ${pluralize('day', ref.days)}`
    //     setSelected(temp)
    // }

    const editPrescription = prs => {
        if (local) {
            let temp = [...selected]

            temp[index].dosage = prs.dosage
            temp[index].frequency = prs.frequency
            temp[index].days = prs.days
            temp[index].quantity = prs.quantity
            temp[index].instructions = `${prs.dosage} x ${prs.frequency} for ${prs.days} ${pluralize('day', prs.days)}`

            setSelected(temp)
        } else {
            prs.instructions = `${prs.dosage} x ${prs.frequency} for ${prs.days} ${pluralize('day', prs.days)}`            
            setPres({...prs})

            setTimeout(updatePrescription, 300)
        }
    }

    const editQuantity = prs => {
        let temp = [...selected]

        temp[index].quantity = prs.quantity
        temp[index].instructions = ''
        
        setSelected(temp)
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const clear = () => setSelected([])

    const selectOthers = () => {
        toggleOthers()
        refresh()
    }

    const editPrevPres = prs => {
        setPres(prs)
        setLocal(false)
        togglePres()
    }

    const cancelItem = prs => {
        setPres(prs)

        if (prs?.cancelled)
            toggleCancelBilledItem()
        else
            toggleCancel()
    }

    const performCancel = rsn => {
        setReason(rsn)
        toggleCancelBilledItem()
    }

    const issue = prs => {
        setPres(prs)
        toggleBatchPicker()
    }

    const confirmIssue = batchNo => {
        issuePrescription({ id: pres?._id, batchNo, store: batchNo ? pharmacyStoreId : undefined })
    }

    const returnPres = prs => {
        setPres(prs)
        returnPrescription()
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <Link to={`${source}/${id}/patients/${order?.patient?._id}`}>
                        <button className="text-white mr-2 hisani-btn-green font-medium rounded-lg text-sm px-5 py-2.5 text-center">Patient Details</button>
                    </Link>
                    {
                        shift &&
                        <>
                            <button onClick={ billPatient } className="text-white hisani-btn-orange mr-3 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Send Patient</button>
                            <button onClick={ clear } className="text-white bg-gray-500 hover:bg-gray-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Clear Items</button>
                        </>
                    }
                </div>
            }
            width={800}>
            <p className="font-extrabold text-3xl mb-0">Medication</p>
            <p className="text-gray-500 text-xs italic">{order?.patient?.name ?? 'Patient'}'s visit {order?.date && `on ${stringifyDate(order?.date)}`}</p>
            {
                prescription &&
                <EditPrescription
                    onPerform={ editPrescription }
                    prescription={ pres }
                    visible={ prescription }
                    toggle={ togglePres } />
            }
            {
                editQty &&
                <EditDrugsQty
                    onPerform={ editQuantity }
                    prescription={ pres }
                    visible={ editQty }
                    toggle={ toggleQty } />
            }
            {
                cancel &&
                <CancelItem
                    item={ pres }
                    onPerform={ () => {
                        fetchBillItems()
                        onPerform()
                    } }
                    loading={ cLoading }
                    action={ performCancel }
                    visible={ cancel }
                    toggle={ toggleCancel } />
            }
            {
                batchPicker &&
                <IssueBatchPicker
                    visible={ batchPicker }
                    toggle={ toggleBatchPicker }
                    itemId={ pres?.ref?.ref?._id }
                    storeId={ pharmacyStoreId }
                    quantity={ pres ? pres.quantity * conversionRate(pres.conversionRate) : 0 }
                    issuedIn={ pres?.ref?.ref?.issuedIn }
                    loading={ ipLoading }
                    onConfirm={ confirmIssue } />
            }
            {
                elevated &&
                <Elevated
                    app="regular"
                    onSuccess={onSuccess}
                    toggle={toggleElevated}
                    visible={elevated} />
            }
            <Tabs onChange={ setTab } activeKey={ tab } className="min-h-full">
                <TabPane key="0" tab="Send New">
                    {
                        shift ?
                            <>
                                <div className="w-full md:w-1/2 mb-6 md:mb-0">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                        Select category
                                    </label>
                                    <Select
                                        inputId="single-select-example"
                                        className="single-select"
                                        classNamePrefix="react-select"
                                        onChange={ onCategoryChange }
                                        value={ cat }
                                        options={ ['All', ...categories].map(cat => ({ label: cat, value: cat })) }
                                        placeholder="Choose Category" />
                                </div>
                                <div className="w-full my-6 md:mb-0">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                        Prescribe Items
                                    </label>
                                    <ReactSelect
                                        isMulti
                                        onInputChange={search}
                                        onChange={ setSelected }
                                        value={ selected }
                                        isLoading={ iLoading }
                                        styles={{
                                            option: (styles, { data }) => { 
                                                let { current } = data
                                                return {
                                                    ...styles, 
                                                    backgroundColor: current <= 0 ? undefined : styles.backgroundColor,
                                                    color: current <= 0 ? 'gray' : styles.color,
                                                    cursor: current <= 0 ? 'not-allowed' : 'default'
                                                }
                                            }
                                        }}
                                        options={ items?.map(itm => ({ label: `${itm.name}${itm.current <= 0 ? ` - Ksh. ${itm.price} (Out of Stock)` : '' }`, value: itm._id, ...itm, quantity: 1, dosage: 1, days: 1, frequency: 1, instructions: '1 x 1 for 1 day', isDisabled: itm?.current <= 0 })) }
                                        placeholder="Add items to the patient's bill" />
                                </div>
                                <div className="p-3 mt-4">
                                    {
                                        selected.length > 0 ?
                                            <div className="overflow-x-auto">
                                                <table className="table-auto w-full">
                                                    {/* Table header */}
                                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                        <tr>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-center">{select ? 'Selected' : 'No'}</div>
                                                            </th>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-left">Item</div>
                                                            </th>
                                                                {
                                                                    selected.some(item => item.ref) &&
                                                                    <th className="p-2">
                                                                        <div className="font-semibold text-center">Stock Level</div>
                                                                    </th>
                                                                }
                                                            <th className="p-2">
                                                                <div className="font-semibold text-center">Quantity</div>
                                                            </th>
                                                            <th className="p-2">
                                                                <div className="font-semibold text-center">Total</div>
                                                            </th>
                                                            <th className="p-2" />
                                                        </tr>
                                                    </thead>
                                                    {/* Table body */}
                                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                        {
                                                            selected.map((item, key) =>
                                                                <tr>
                                                                    <td className="p-2">
                                                                        <div className="text-center">{((page - 1) * limit) + (key + 1)}.</div>
                                                                    </td>
                                                                    <td className="p-2">
                                                                        <div className="">{ item.name }</div>
                                                                        { item.instructions && <p className="text-gray-500 mb-0 text-xs italic">{ item.instructions }</p> }
                                                                    </td>
                                                                    {
                                                                        selected.some(item => item.ref) &&
                                                                        <th className="p-2">
                                                                            <div className={ `font-semibold text-center ${ item?.quantity > item?.current ? 'text-red-500' : 'text-green-500' }` }>{ item?.ref ? `${item?.current} ${pluralize(item?.ref?.issuedIn, item?.current)}` : 'N/A' }</div>
                                                                        </th>
                                                                    }
                                                                    <td className="p-2 text-center">
                                                                        <span>{ item.quantity } x Ksh. { item.price }</span>
                                                                    </td>
                                                                    <td className="p-2 text-center">
                                                                        <span>Ksh. { item.quantity * item.price }</span>
                                                                    </td>
                                                                    <td className="p-2 text-center">
                                                                        <Dropdown
                                                                            overlay={
                                                                                <Menu>
                                                                                    <Menu.Item onClick={ () => {
                                                                                        setPres(item)
                                                                                        setLocal(true)
                                                                                        setIndex(key)
                                                                                        togglePres()
                                                                                    } }>Write Prescription</Menu.Item>
                                                                                    <Menu.Item onClick={ () => {
                                                                                        setPres(item)
                                                                                        setLocal(true)
                                                                                        setIndex(key)
                                                                                        toggleQty()
                                                                                    } }>Edit Quantity</Menu.Item>
                                                                                </Menu>
                                                                            }>
                                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                                        </Dropdown>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        }
                                                    </tbody>
                                                </table>
                                                <div className="flex flex-col items-end mb-3 p-3">
                                                    <p className="font-semibold text-lg underline mb-2">Total Selected</p>
                                                    <p>Total: Ksh. { normalize(selected.reduce((total, itm) => total + (itm.price * itm.quantity), 0)) }</p>
                                                </div>
                                            </div>
                                            :
                                            <div className="w-full h-16 mt-4 flex items-center justify-center">
                                                <p className="font-semibold text-base">You can add items by selecting from the list above</p>
                                            </div>
                                    }
                                </div>
                            </>
                        : 
                            <div className="w-full h-16 mt-4 flex items-center justify-center">
                                <p className="font-semibold text-base">Please start or select a shift to proceed</p>
                            </div>
                    }
                </TabPane>
                <TabPane key="1" tab={ <Badge count={ count }>Previous Medication</Badge> }>
                    <div className="sm:flex sm:justify-between sm:items-center mb-2">
                        <span>
                            <input
                                onChange={e => searchSent(e.target.value)}
                                className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                id="grid-ssearch"
                                type="text"
                                placeholder="Search for item" />
                            <span>
                                <input
                                    onChange={ selectOthers }
                                    checked={ includeOthers }
                                    type="checkbox"
                                    className="form-checkbox cursor-pointer h-5 w-5" />
                                <span onClick={ selectOthers } className="text-gray-500 cursor-pointer text-xs italic ml-2">Show medication from outside this visit</span>
                            </span>
                        </span>
                        {
                            includeOthers &&
                            <RangePicker onChange={selectRange} />
                        }
                    </div>
                    <div className="">
                        <Receive
                            progress
                            loading={itLoading}
                            error={itError}>
                            {
                                billItems.length > 0 ?
                                    <div className="overflow-x-auto">
                                        <table className="table-auto w-full">
                                            {/* Table header */}
                                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                                <tr>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-center">No</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Item</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Status</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Quantity</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Price</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-left">Sent By</div>
                                                    </th>
                                                    <th className="p-2">
                                                        <div className="font-semibold text-center"></div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            {/* Table body */}
                                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                                {
                                                    billItems.map((item, key) =>
                                                        <tr>
                                                            <td className="p-2">
                                                                <div className="text-center">{((itPage - 1) * itLimit) + (key + 1)}.</div>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className="">{ item.name } ({ item.instructions })</div>
                                                                <p className="text-gray-500 my-0 text-xs italic">Sent On { stringifyDate(item.date) }</p>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className={ `text-${ item.cancelled ? 'gray' : item.served ? 'green' : item.seen ? 'green' : 'yellow'}-500` }>{ item.cancelled ? 'Cancelled' : item.served ? 'Issued' : item.seen ? 'Seen' : 'Pending' } ({ order?.closed ? 'Paid' : 'Unpaid' })</div>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className="">{ item.quantity * conversionRate(item.conversionRate) } { pluralize(item?.ref?.ref?.issuedIn ?? '', item.quantity * conversionRate(item.conversionRate)) }</div>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className="">{ item.quantity * conversionRate(item.conversionRate) * item.unitPrice }</div>
                                                            </td>
                                                            <td className="p-2">
                                                                <div className="">{ item.sentBy?.name }</div>
                                                            </td>
                                                            <td className="p-2">
                                                                {
                                                                    shift && !includeOthers &&
                                                                    <Dropdown
                                                                        overlay={
                                                                            <Menu>
                                                                                {
                                                                                    order?.closed ? 
                                                                                        (item?.seen | item?.served) ?
                                                                                        <Menu.Item onClick={ () => Confirm({
                                                                                            title: 'Record Return?',
                                                                                            message: `This will restock ${item.quantity * conversionRate(item.conversionRate)} ${pluralize(item?.ref?.ref?.issuedIn ?? '', item.quantity * conversionRate(item.conversionRate))} of ${item?.ref?.ref?.name ?? item?.ref?.name} to pharmacy inventory`,
                                                                                            okText: 'Return Prescription',
                                                                                            onOk: () => returnPres(item)
                                                                                        }) }>Return Prescription</Menu.Item> :
                                                                                        <Menu.Item onClick={ () => issue(item) }>Issue Prescription</Menu.Item> :
                                                                                    <>
                                                                                        <Menu.Item onClick={ () => elevate(() => Confirm({
                                                                                            title: item.cancelled ? 'Undo Cancel?' : 'Cancel item',
                                                                                            message: item.cancelled ? 'This will undo the cancel action performed on this item.' : 'Please confirm before cancelling the selected item',
                                                                                            okText: item.cancelled ? 'Undo Cancel?' : 'Cancel item',
                                                                                            onOk: () => cancelItem(item)
                                                                                        })) }> { item?.cancelled ? 'Undo Cancel' : 'Cancel' }</Menu.Item>
                                                                                        {
                                                                                            !item?.cancelled &&
                                                                                            <Menu.Item onClick={ () => elevate(() => Confirm({
                                                                                                title: 'Edit Prescription?',
                                                                                                message: 'Please confirm before editing this prescription',
                                                                                                okText: 'Edit Prescription',
                                                                                                onOk: () => editPrevPres(item)
                                                                                            })) }>Edit Prescription</Menu.Item>
                                                                                        }
                                                                                    </>
                                                                                }
                                                                            </Menu>
                                                                        }>
                                                                        <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                                    </Dropdown>
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                    :
                                    <div className="w-full h-16 mt-4 flex items-center justify-center">
                                        <p className="font-semibold text-base">The patient wasn't sent for any medication</p>
                                    </div>
                            }
                        </Receive>
                        { Paginate }
                    </div>
                    <Switch>
                        <Route path={ `${source}/${id}/patients/:id` }>
                            <PatientDetails source={ `${source}/${id}/patients/${id}` } />
                        </Route>
                    </Switch>
                </TabPane>
            </Tabs>
        </Drawer>
    )
}

export default SentMedication